import PropTypes from 'prop-types';
import React from 'react';

import cls from 'classnames/bind';

import { lang as l10nLang, formatMessage } from '@ott/l10n';
import formatMoney from '@ott/utility-format-money';

import s from './Money.scss';

const spaceRegexp = /\u00A0/g;

const currenciesMap = {
  'uk-ua': 'UAH',
  'ru-ua': 'UAH',
  'kk-kz': 'KZT',
  'ru-kz': 'KZT',
  'en-gb': 'GBP',
  'en-us': 'USD',
  'en-ie': 'EUR',
  'ru-by': 'BYN',
  'en-ch': 'CHF',
  'fr-ch': 'CHF',
  'de-ch': 'CHF',
  de: 'EUR',
  es: 'EUR',
  kz: 'KZT',
  tr: 'TRY',
  pl: 'PLN',
  br: 'EUR',
  az: 'EUR',
  ru: 'RUB',
  fr: 'EUR',
};

const getCurrency = (lang) => currenciesMap[lang];

const cx = cls.bind(s);
class Money extends React.PureComponent {
  static propTypes = {
    amount: PropTypes.number,
    lang: PropTypes.string,
    numberFormatOptions: PropTypes.shape({
      minimumFractionDigits: PropTypes.number,
      maximumFractionDigits: PropTypes.number,
    }),
    isFat: PropTypes.bool,
    showPlusSign: PropTypes.bool,
    useSymbol: PropTypes.bool,
    symbolReplacer: PropTypes.string,
    isRange: PropTypes.bool,
    asNumber: PropTypes.bool,
    customCurrencyIconUrl: PropTypes.string, // берется из ВЛ конфига
  };

  static defaultProps = {
    lang: l10nLang,
    numberFormatOptions: {},
    amount: 0,
    isFat: false,
    showPlusSign: false,
    useSymbol: true,
    symbolReplacer: '',
    isRange: false, // Используется в ВЛ
    asNumber: false,
  };

  render() {
    const {
      lang,
      numberFormatOptions,
      currency = getCurrency(lang), // значение по умолчанию, если не передали currency явно
      amount,
      isFat,
      showPlusSign,
      useSymbol,
      style,
      className,
      symbolReplacer,
      asNumber,
      customCurrencyIconUrl,
    } = this.props;

    const formattedAmount = formatMoney(Math.abs(amount), {
      currencyCode: currency,
      lang,
      numberFormatOptions,
      useSymbol,
      asNumber,
    });

    const getContentNode = () => {
      const symbolMatch = /[^\w\s]+$/gi.exec(formattedAmount);
      const contentNode = [];

      if (amount < 0) {
        contentNode.push(<span key="sign">&minus;</span>);
      }

      if (amount >= 0 && showPlusSign) {
        contentNode.push(<span key="sign">+</span>);
      }

      if (!symbolMatch) {
        contentNode.push(<span key="formattedAmount">{formattedAmount}</span>);

        return contentNode;
      }

      const symbol = symbolMatch[0];
      const before = formattedAmount.substr(0, symbolMatch.index);
      const after = formattedAmount.substr(symbolMatch.index + symbol.length);

      const symbolNode = customCurrencyIconUrl ? (
        <span key="customMoneySymbol" className={cx('customMoneySymbol')}>
          <img
            key="customCurrencyIcon"
            className={cx('customCurrencyIcon')}
            src={customCurrencyIconUrl}
            alt="customCurrencyIcon"
          />
        </span>
      ) : (
        <span key="symbol" className={cx('symbol')}>
          {symbolReplacer
            ? formatMessage(
                {
                  id: 'money.WLSymbolReplacer',
                  defaultMessage: symbolReplacer,
                },
                { amount: parseInt(before.replace(spaceRegexp, ''), 10) }
              )
            : symbol}
        </span>
      );

      contentNode.push(
        <span key="before">{before}</span>,
        symbolNode,
        <span key="after">{after}</span>
      );

      return contentNode;
    };

    const contentNode = getContentNode();

    const dataAttributes = Object.keys(this.props).reduce((acc, key) => {
      if (key.startsWith('data-')) {
        const { [key]: dataAttribute } = this.props;
        acc[key] = dataAttribute;
      }

      return acc;
    }, {});

    return (
      <span
        className={cx('Money', className, {
          'Money--isFat': isFat,
        })}
        style={style}
        {...dataAttributes}
      >
        {contentNode}
      </span>
    );
  }
}

let Constructor = Money;
try {
  if (__WHITE_LABEL__) {
    class WLMoney extends React.PureComponent {
      constructor(props) {
        super(props);
        this.state = {
          config: null,
        };
        const WLAdapter = require('@ott/white-label/adapter').default;
        WLAdapter.getConfig().then((config) => {
          this.setState({ config });
        });
      }

      render() {
        const { amount, useOriginalMoney, isRange, ...otherProps } = this.props;
        if (useOriginalMoney) {
          return <Money {...this.props} />;
        }
        if (!this.state.config) {
          return null;
        }
        const {
          config: { moneyRate, moneyReplaceSymbol, moneyRangeReplaceSymbol, customCurrencyIconUrl },
        } = this.state;
        let symbolReplacer = moneyReplaceSymbol;
        if (isRange && moneyRangeReplaceSymbol) {
          symbolReplacer = moneyRangeReplaceSymbol;
        }
        let price = amount;
        if (moneyRate) {
          price = amount * moneyRate;
        }

        return (
          <Money
            {...otherProps}
            amount={price}
            symbolReplacer={symbolReplacer}
            customCurrencyIconUrl={customCurrencyIconUrl}
          />
        );
      }
    }

    Constructor = WLMoney;
  }
} catch (e) {
  console.error(e);
}

export default Constructor;

export { getCurrency };
